"use client";

import { forwardRef, useId } from "react";
import { cn } from "@/lib/utils";

interface RadioProps extends Omit<React.ComponentProps<"input">, "type"> {
  label?: string;
}

const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ className, label, id: externalId, ...props }, ref) => {
    const autoId = useId();
    const id = externalId || autoId;

    return (
      <div className="flex items-center gap-[var(--space-2)]">
        <div className="relative flex items-center justify-center">
          <input
            ref={ref}
            id={id}
            type="radio"
            data-slot="radio"
            className={cn(
              [
                "peer size-4 shrink-0 appearance-none",
                "rounded-full border",
                "border-[var(--color-border-default)]",
                "bg-transparent cursor-pointer",
                "transition-colors duration-[var(--duration-fast)]",
                "focus-visible:outline-none",
                "focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary-muted)]",
                "focus-visible:border-[var(--color-border-focus)]",
                "checked:border-[var(--color-primary)]",
                "disabled:pointer-events-none disabled:opacity-50",
              ].join(" "),
              className
            )}
            {...props}
          />
          <div
            className="pointer-events-none absolute size-2 rounded-full bg-[var(--color-primary)] opacity-0 peer-checked:opacity-100 transition-opacity"
            aria-hidden="true"
          />
        </div>
        {label && (
          <label
            htmlFor={id}
            className="cursor-pointer text-[length:var(--font-size-sm)] text-[var(--color-text-primary)] select-none peer-disabled:pointer-events-none peer-disabled:opacity-50"
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);
Radio.displayName = "Radio";

/* ── Radio Group ────────────────────────────────────────────── */

interface RadioGroupProps {
  label?: string;
  children: React.ReactNode;
  className?: string;
}

function RadioGroup({ label, children, className }: RadioGroupProps) {
  return (
    <fieldset
      role="radiogroup"
      className={cn("flex flex-col gap-[var(--space-2)]", className)}
    >
      {label && (
        <legend className="text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)] text-[var(--color-text-primary)] mb-[var(--space-1)]">
          {label}
        </legend>
      )}
      {children}
    </fieldset>
  );
}

export { Radio, RadioGroup };
export type { RadioProps, RadioGroupProps };
